import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import Heading from '@bibliocommons/heading';
import Divider from 'app/components/bibs/shared/Divider';
import { UI_CONTAINER_TITLE } from 'app/constants/AnalyticsGa4Constants';
import { EVENTS_URL } from 'app/constants/EventsConstants';
import { eventShape } from 'app/shapes/events';
import {
  selectEventAudienceEntities,
  selectLocationEntities,
  selectEventProgramsEntities
} from 'app/selectors/EntitiesSelector';
import EventsList from '../EventsList';

import './RelatedEvents.scss';

const ONLINE_LOCATION = 'BC_VIRTUAL';

export default function RelatedEvents({ event, trackEventLinkClick, trackViewAllEventsLinkClick }) {
  const programId = event?.getIn(['definition', 'programId']);
  const audienceId = event?.getIn(['definition', 'audienceIds', 0]);
  const isVirtual = event?.getIn(['definition', 'isVirtual']);
  const locationId = isVirtual ? ONLINE_LOCATION : event?.getIn(['definition', 'branchLocationId']);
  const eventProgram = useSelector(selectEventProgramsEntities)?.get(programId);
  const eventAudience = useSelector(selectEventAudienceEntities)?.get(audienceId);
  const eventLocation = useSelector(selectLocationEntities)?.get(locationId);

  const relatedEventsByProgram = useSelector(state =>
    state.getIn(['events', 'eventsDetails', 'relatedEventsByProgram'])
  );
  const viewAllProgramsHref = `${EVENTS_URL}?programs=${programId}`;
  const programHeading = (
    <FormattedMessage
      id="events.related.program"
      defaultMessage="{programName} Events"
      values={{ programName: eventProgram?.get('name') }}
    />
  );

  const relatedEventsByAudience = useSelector(state =>
    state.getIn(['events', 'eventsDetails', 'relatedEventsByAudience'])
  );
  const viewAllAudiencesHref = `${EVENTS_URL}?audiences=${audienceId}&locations=${locationId}`;
  const audienceHeading = isVirtual ? (
    <FormattedMessage
      id="events.related.audience_online"
      defaultMessage="{audienceName} events online"
      values={{
        audienceName: eventAudience?.get('name')
      }}
    />
  ) : (
    <FormattedMessage
      id="events.related.audience_and_location"
      defaultMessage="{audienceName} Events at {locationName}"
      values={{
        audienceName: eventAudience?.get('name'),
        locationName: eventLocation?.get('name')
      }}
    />
  );

  function renderRelatedEvents() {
    const hasProgramEvents = !!(programId && relatedEventsByProgram?.get('eventIds')?.size);
    const hasAudienceEvents = !!(audienceId && locationId && relatedEventsByAudience?.get('eventIds')?.size);

    if (!hasProgramEvents && !hasAudienceEvents) {
      return null;
    }

    return (
      <div className="cp-related-events">
        <Divider />
        <div className="col-lg-3 col-xl-3 related-section-heading">
          <Heading className="related-events-heading" tagName="h2" size="moderate" variant>
            <FormattedMessage id="related_events" defaultMessage="Related events" />
          </Heading>
        </div>
        <div className="col-lg-9 col-xl-9 related-events-list">
          <div className="related-events-section">
            {hasAudienceEvents && (
              <EventsList
                sectionHeading={audienceHeading}
                relatedEvents={relatedEventsByAudience}
                viewAllHref={viewAllAudiencesHref}
                uiContainerTitle={UI_CONTAINER_TITLE.EVENT_RELATED_AUDIENCE_LOCATION}
                trackEventLinkClick={trackEventLinkClick}
                trackViewAllEventsLinkClick={trackViewAllEventsLinkClick}
              />
            )}
            {hasProgramEvents && (
              <EventsList
                sectionHeading={programHeading}
                relatedEvents={relatedEventsByProgram}
                viewAllHref={viewAllProgramsHref}
                uiContainerTitle={UI_CONTAINER_TITLE.EVENT_RELATED_PROGRAM}
                trackEventLinkClick={trackEventLinkClick}
                trackViewAllEventsLinkClick={trackViewAllEventsLinkClick}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  return renderRelatedEvents();
}

RelatedEvents.propTypes = {
  event: eventShape.isRequired,
  trackEventLinkClick: PropTypes.func.isRequired,
  trackViewAllEventsLinkClick: PropTypes.func.isRequired
};
